import { PriceScaleMode, Time } from 'lightweight-charts'
import { generatePath, type NavigateFunction } from 'react-router-dom'
import ROUTES from '../../../assets/routes'
import { AppDispatch, RootState } from '../../../store'
import { ChartType, chartTypeFromJSON } from '../../../proto/generated/chart'
import { Indicator } from '../../../proto/generated/indicator'
import { Security } from '../../../proto/generated/security'
import { Exchange, exchangeFromJSON, TimeBucket_TimeSeries, timeBucket_TimeSeriesFromJSON } from '../../../proto/generated/common'
import { SuperChartConfiguration_DateRange, SuperChartConfiguration_TimeBucket } from '../../../proto/generated/superchart-page'
import {
    removeIndicator,
    updateChartConfig,
    updateChartTimeBucket,
    updateChartType,
    updateComparableSecurities,
    updateGroupInputs,
} from '../../../reducer/chart.reducer'
import {
    selectChartComparableSecurities,
    selectChartConfig,
    selectChartTypes,
    selectDateRanges,
    selectIndicators,
    selectTimeBuckets,
} from '../../../selectors/chart'
import { selectActiveLayout, selectActiveLayoutId } from '../../../selectors/home'
import { selectUserDashboards } from '../../../selectors/layout'
import { DashboardNamespace } from '../../../proto/generated/user-preference'
import type { IWidgetDashboard } from '../../widgetDashboard/widgetDashboard.interface'
import {
    applyLayoutCommand,
    isLayoutCommandOp,
    normalizeLayoutOp,
    orderedPaneSlots,
    resolvePaneSlot,
} from './chartLayoutCommands'
import { getSecurityAsync, getSecurityKey, resolveSecurityBySymbol } from '../../../util/security'
import { buildComparableSecurityPanePlacements } from '../../../util/chart'
import { sanitizeId } from '../../../util/indicators'
import { findPatternIndicator, isPatternIndicator, normalizeIndicatorCode } from '../../../util/patternIndicators'
import { SeriesGroupCategory } from '../../../pages/home'
import Chart from '../chart'
import { ComparableSecurityPanePlacement } from '../chart.typings'
import { DrawingDisplayType, DrawingTypes } from '../drawingTools/drawing.interface'
import store from '../../../store'
import {
    type ChartCommandApplyResult,
    type ChartCommandFailureReason,
    chartCommandFailureMessage,
    chartCommandSuccessMessage,
    emptyChartCommandApplyResult,
    layoutCommandFailureMessage,
    layoutCommandSuccessMessage,
} from './chartCommandMessages'

export type { ChartCommandApplyResult } from './chartCommandMessages'

export type ChartCommand = {
    op: string
    [key: string]: unknown
}

/** Switch to a persisted user dashboard layout (not an in-session preset). */
export type LoadSavedLayoutCommand = {
    op: 'load_saved_layout'
    layout_id?: string
    name?: string
    default?: boolean
}

export const LOAD_SAVED_LAYOUT_OP = 'load_saved_layout' as const

export type ChartCommandApplyDeps = {
    viewKey: string
    chart: Chart | undefined
    dispatch: AppDispatch
    setPrimarySecurity: (
        key: string,
        securities: Security[],
        expressionMode: boolean,
        displayName?: string,
    ) => void
    dashboard?: Pick<IWidgetDashboard, 'splitPane' | 'deletePane'>
    /** Needed so a command can address a chart in a pane other than the focused one. */
    getChart?: (viewKey: string) => Chart | undefined
    /** Required for `load_saved_layout` — URL navigation is the authoritative switch path. */
    navigate?: NavigateFunction
}

function chartSavedDashboards(state: RootState) {
    return selectUserDashboards(state).filter(
        (dashboard) => !dashboard.isPredefined && dashboard.namespace === DashboardNamespace.CHART,
    )
}

/** Resolve a saved layout id from command fields against hydrated user dashboards. */
export function resolveSavedLayoutId(state: RootState, command: ChartCommand): string | undefined {
    const dashboards = chartSavedDashboards(state)
    if (command.default === true) {
        return dashboards.find((dashboard) => dashboard.isDefault)?.id
    }

    const layoutId = String(command.layout_id ?? '').trim()
    if (layoutId) {
        return dashboards.find((dashboard) => dashboard.id === layoutId)?.id
    }

    const name = String(command.name ?? '').trim()
    if (name) {
        const normalized = name.toLowerCase()
        return dashboards.find((dashboard) => dashboard.name.toLowerCase() === normalized)?.id
    }

    return undefined
}

function savedLayoutName(state: RootState, layoutId: string): string | undefined {
    return chartSavedDashboards(state).find((dashboard) => dashboard.id === layoutId)?.name
}

function cloneIndicatorWithInputs(
    template: Indicator,
    inputs: Record<string, unknown> | undefined,
    pane: 'overlay' | 'new',
): Indicator {
    const mergedInputs = (template.inputs || []).map((input) => {
        const code = input.code || input.title
        const raw = inputs?.[code] ?? inputs?.[sanitizeId(code)]
        if (raw == null || raw === '') return input
        return { ...input, defval: String(raw) }
    })
    return {
        ...template,
        inputs: mergedInputs,
        type: pane === 'overlay' ? 'OVERLAY' : 'NON_OVERLAY',
    }
}

function parseExchange(value: unknown): Exchange | null {
    if (value == null || value === '') return null
    const parsed = exchangeFromJSON(value)
    if (parsed === Exchange.EXCHANGE_UNKNOWN || parsed === Exchange.UNRECOGNIZED) return null
    return parsed
}

const NUMBER_WORDS: Record<string, number> = {
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
    seven: 7,
    eight: 8,
    nine: 9,
    ten: 10,
    fifteen: 15,
    thirty: 30,
    sixty: 60,
}

type DurationIntent = {
    series: TimeBucket_TimeSeries
    value: number
}

function seriesFromUnit(unit: string): TimeBucket_TimeSeries | null {
    switch (unit) {
        case 's':
        case 'sec':
        case 'secs':
        case 'second':
        case 'seconds':
            return TimeBucket_TimeSeries.SECOND
        case 'm':
        case 'min':
        case 'mins':
        case 'minute':
        case 'minutes':
            return TimeBucket_TimeSeries.MINUTE
        case 'h':
        case 'hr':
        case 'hrs':
        case 'hour':
        case 'hours':
            return TimeBucket_TimeSeries.HOUR
        case 'd':
        case 'day':
        case 'days':
            return TimeBucket_TimeSeries.DAY
        case 'w':
        case 'wk':
        case 'week':
        case 'weeks':
            return TimeBucket_TimeSeries.WEEK
        case 'mo':
        case 'month':
        case 'months':
            return TimeBucket_TimeSeries.MONTH
        case 'y':
        case 'yr':
        case 'year':
        case 'years':
            return TimeBucket_TimeSeries.YEAR
        default:
            return null
    }
}

function parseDurationIntent(raw: string): DurationIntent | null {
    const text = raw.trim().toLowerCase()
    if (!text) return null

    // Agents emit abbreviated units as often as full ones (FIFTEEN_MIN, 15_MINUTES,
    // "one hr"), so match any unit token and let seriesFromUnit decide what is valid.
    const enumMatch = text.replace(/[\s-]+/g, '_').match(/^([a-z0-9]+)_([a-z]+)$/)
    if (enumMatch) {
        const word = enumMatch[1]
        const series = seriesFromUnit(enumMatch[2])
        const value = NUMBER_WORDS[word] ?? (/^\d+$/.test(word) ? Number(word) : null)
        if (series != null && value != null && value > 0) {
            return { series, value }
        }
    }

    const compact = text.replace(/[\s_-]+/g, '')
    const compactMatch = compact.match(
        /^(\d+)(s|sec|secs|second|seconds|m|min|mins|minute|minutes|h|hr|hrs|hour|hours|d|day|days|w|wk|week|weeks|mo|month|months|y|yr|year|years)?$/,
    )
    if (compactMatch) {
        const value = Number(compactMatch[1])
        const unit = compactMatch[2] || 'min'
        const series = seriesFromUnit(unit)
        if (series != null && value > 0) {
            return { series, value }
        }
    }

    return null
}

function matchBucketByStructure(
    buckets: SuperChartConfiguration_TimeBucket[],
    intent: DurationIntent,
): SuperChartConfiguration_TimeBucket | undefined {
    return buckets.find((item) => item.timeSeries === intent.series && item.timeValue === intent.value)
}

function timeBucketsMatch(
    a: SuperChartConfiguration_TimeBucket | null | undefined,
    b: SuperChartConfiguration_TimeBucket | null | undefined,
): boolean {
    if (!a || !b) return false
    if (a.code && b.code && a.code === b.code) return true
    return a.timeSeries === b.timeSeries && a.timeValue === b.timeValue
}

function resolveTimeBucketForChart(
    buckets: SuperChartConfiguration_TimeBucket[],
    command: ChartCommand,
    viewKey: string,
): SuperChartConfiguration_TimeBucket | undefined {
    const resolved = resolveTimeBucket(buckets, command)
    if (resolved) return resolved

    const current = selectChartConfig(viewKey)(store.getState())?.timeBucket
    if (!current) return undefined

    const rawCode = String(command.code || '').trim()
    if (rawCode && current.code && rawCode.toUpperCase() === current.code.toUpperCase()) {
        return current
    }

    const intent = parseDurationIntent(rawCode)
    if (intent && current.timeSeries === intent.series && current.timeValue === intent.value) {
        return current
    }

    return undefined
}

function resolveTimeBucket(
    buckets: SuperChartConfiguration_TimeBucket[],
    command: ChartCommand,
): SuperChartConfiguration_TimeBucket | undefined {
    if (command.series != null && command.value != null) {
        const series = timeBucket_TimeSeriesFromJSON(command.series)
        const value = Number(command.value)
        if (series !== TimeBucket_TimeSeries.UNRECOGNIZED && Number.isFinite(value) && value > 0) {
            const byStructure = matchBucketByStructure(buckets, { series, value })
            if (byStructure) return byStructure
        }
    }

    const rawCode = String(command.code || '').trim()
    if (!rawCode) return undefined

    const exact = buckets.find((item) => item.code === rawCode)
    if (exact) return exact

    const upper = rawCode.toUpperCase()
    const byUpperCode = buckets.find((item) => item.code.toUpperCase() === upper)
    if (byUpperCode) return byUpperCode

    const intent = parseDurationIntent(rawCode)
    if (intent) {
        const byIntent = matchBucketByStructure(buckets, intent)
        if (byIntent) return byIntent
    }

    const lowered = rawCode.toLowerCase()
    return buckets.find((item) => item.name?.toLowerCase() === lowered)
}

function resolveDateRange(
    ranges: SuperChartConfiguration_DateRange[],
    rawCode: string,
): SuperChartConfiguration_DateRange | undefined {
    const code = rawCode.trim()
    if (!code) return undefined
    const upper = code.toUpperCase().replace(/[\s-]+/g, '_')
    return (
        ranges.find((item) => item.code === code) ||
        ranges.find((item) => item.code?.toUpperCase() === upper) ||
        ranges.find((item) => item.name?.toLowerCase() === code.toLowerCase())
    )
}

function findSeriesGroupId(viewKey: string, command: ChartCommand): string | undefined {
    const config = selectChartConfig(viewKey)(store.getState())
    if (!config) return undefined
    const explicitId = String(command.series_group_id || '')
    if (explicitId && config.seriesGroups[explicitId]) {
        return explicitId
    }
    const code = sanitizeId(String(command.code || ''))
    if (!code) return undefined
    const match = Object.values(config.seriesGroups).find(
        (group) =>
            sanitizeId(group.scriptCode) === code &&
            [SeriesGroupCategory.SERIES_INDICATOR, SeriesGroupCategory.SERIES_VOLUME].includes(group.category),
    )
    return match?.id
}

/**
 * Accepts epoch seconds, epoch millis, or an ISO/`YYYY-MM-DD` string and
 * normalises to the epoch seconds the chart's time scale works in.
 */
function parseTimeSeconds(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value > 1e11 ? Math.floor(value / 1000) : Math.floor(value)
    }
    const text = String(value ?? '').trim()
    if (!text) return null
    if (/^\d+$/.test(text)) return parseTimeSeconds(Number(text))
    const parsed = Date.parse(text)
    if (Number.isNaN(parsed)) return null
    return Math.floor(parsed / 1000)
}

function parsePrice(value: unknown): number | null {
    const price = Number(value)
    return Number.isFinite(price) ? price : null
}

type DrawingPoint = { id: string; x: Time; y: number }

function makePoint(id: string, time: number, price: number): DrawingPoint {
    return { id, x: time as Time, y: price }
}

function latestChartTime(chart: Chart): number | null {
    const data = chart.getRawData() || []
    const last = data[data.length - 1]
    return last ? parseTimeSeconds(last.time) : null
}

function latestChartPrice(chart: Chart): number | null {
    const data = chart.getRawData() || []
    const last = data[data.length - 1]
    return last && Number.isFinite(last.close) ? Number(last.close) : null
}

/**
 * Coordinates for a drawing must be supplied as `initialState`; passing only
 * `settings` leaves the tool in interactive DRAW mode waiting for mouse clicks.
 */
const LINE_VARIANTS = new Set<DrawingTypes>([
    'trendLine',
    'ray',
    'arrow',
    'extendedLine',
    'trendAngle',
    'infoLine',
    'simpleLine',
])

const TWO_POINT_TOOLS = new Set<DrawingTypes>(['rectangle', 'fibonacci', 'dateRange', 'priceRange', 'forecast'])

type DrawingPlacement = {
    type: DrawingTypes
    options: Record<string, unknown>
    initialState: Record<string, DrawingPoint>
}

type CommandPoint = { time?: unknown; price?: unknown }

function readPoints(command: ChartCommand): CommandPoint[] {
    const raw = command.points
    if (!Array.isArray(raw)) return []
    return raw.filter((item): item is CommandPoint => !!item && typeof item === 'object')
}

function buildDrawingPlacement(command: ChartCommand, chart: Chart): DrawingPlacement | null {
    const type = String(command.type || '').trim() as DrawingTypes
    if (!type) return null
    const points = readPoints(command)

    if (type === 'horizontalLine' || type === 'horizontalRay') {
        const price = parsePrice(command.price ?? points[0]?.price)
        if (price == null) return null
        const time = parseTimeSeconds(command.time ?? points[0]?.time) ?? latestChartTime(chart)
        if (time == null) return null
        return { type, options: { drawingType: type }, initialState: { p1: makePoint('p1', time, price) } }
    }

    if (type === 'verticalLine' || type === 'crossLine') {
        const time = parseTimeSeconds(command.time ?? points[0]?.time)
        if (time == null) return null
        const price = parsePrice(command.price ?? points[0]?.price) ?? latestChartPrice(chart)
        if (price == null) return null
        return { type, options: {}, initialState: { p1: makePoint('p1', time, price) } }
    }

    if (LINE_VARIANTS.has(type) || TWO_POINT_TOOLS.has(type)) {
        if (points.length < 2) return null
        const t1 = parseTimeSeconds(points[0].time)
        const p1 = parsePrice(points[0].price)
        const t2 = parseTimeSeconds(points[1].time)
        const p2 = parsePrice(points[1].price)
        if (t1 == null || p1 == null || t2 == null || p2 == null) return null
        // The rectangle derives its remaining corners from p1/p3.
        const secondId = type === 'rectangle' ? 'p3' : 'p2'
        return {
            type,
            options: LINE_VARIANTS.has(type) ? { drawingType: type } : {},
            initialState: {
                p1: makePoint('p1', t1, p1),
                [secondId]: makePoint(secondId, t2, p2),
            },
        }
    }

    return null
}

const SCALE_MODES: Record<string, PriceScaleMode> = {
    normal: PriceScaleMode.Normal,
    regular: PriceScaleMode.Normal,
    linear: PriceScaleMode.Normal,
    log: PriceScaleMode.Logarithmic,
    logarithmic: PriceScaleMode.Logarithmic,
    percent: PriceScaleMode.Percentage,
    percentage: PriceScaleMode.Percentage,
    indexed: PriceScaleMode.IndexedTo100,
    indexed_to_100: PriceScaleMode.IndexedTo100,
}

const SETTINGS_GROUPS = {
    canvas: 'canvasSettings',
    events: 'eventsSettings',
    scales_and_lines: 'scalesAndLinesSettings',
    status_line: 'statusLineSettings',
} as const

type SettingsGroupKey = keyof typeof SETTINGS_GROUPS

function comparisonPlacement(value: unknown): ComparableSecurityPanePlacement {
    return String(value || '').toLowerCase() === 'new_pane' ? 'new_pane' : 'overlay'
}

function resolveChartTypeFromCommand(command: ChartCommand): ChartType | null {
    const raw = String(command.chart_type || command.type || '').trim()
    if (!raw) return null

    const fromEnum = chartTypeFromJSON(raw)
    if (
        fromEnum !== ChartType.CHART_TYPE_UNKNOWN &&
        fromEnum !== ChartType.UNRECOGNIZED
    ) {
        return fromEnum
    }

    const normalized = raw.toLowerCase().replace(/[\s-]+/g, '_')
    const chartTypes = selectChartTypes(store.getState()) || []
    const match = chartTypes.find(
        (item) =>
            item.code?.toLowerCase() === normalized ||
            item.name?.toLowerCase() === raw.toLowerCase() ||
            item.name?.toLowerCase().replace(/[\s-]+/g, '_') === normalized,
    )
    if (!match?.code) return null

    const resolved = chartTypeFromJSON(match.code)
    if (resolved === ChartType.CHART_TYPE_UNKNOWN || resolved === ChartType.UNRECOGNIZED) {
        // Backend may still expose Columns under the HISTOGRAM code.
        if (match.name?.toLowerCase() === 'columns') {
            return ChartType.COLUMN
        }
        return null
    }
    return resolved
}

function resolveIndicatorTemplate(indicators: Indicator[], rawCode: string): Indicator | undefined {
    const text = rawCode.trim()
    if (!text) return undefined

    const normalized = normalizeIndicatorCode(text)
    let template = indicators.find((item) => normalizeIndicatorCode(item.code) === normalized)
    if (template) return template

    template = findPatternIndicator(text, indicators)
    if (template) return template

    const lower = text.toLowerCase()
    return indicators.find((item) => {
        const name = item.name?.toLowerCase()
        const shortName = item.shortName?.toLowerCase()
        return (
            name === lower
            || shortName === lower
            || normalizeIndicatorCode(item.name) === normalized
            || normalizeIndicatorCode(item.shortName) === normalized
        )
    })
}

function defaultIndicatorPane(template: Indicator, requestedPane: 'overlay' | 'new'): 'overlay' | 'new' {
    if (requestedPane === 'overlay') return 'overlay'
    if (template.type === 'OVERLAY' || isPatternIndicator(template)) return 'overlay'
    return 'new'
}

async function resolveSecurityForCommand(command: ChartCommand): Promise<Security | undefined> {
    const code = String(command.code || command.symbol || '').trim()
    if (!code) return undefined
    const exchange = parseExchange(command.exchange) ?? Exchange.NSE
    return resolveSecurityBySymbol(exchange, code)
}

/** Redux alone does not update the live chart — mirror SecurityCompare + chartManager. */
function syncComparableSecuritiesToChart(viewKey: string, chart: Chart | undefined) {
    if (!chart) return
    const state = store.getState()
    const comparableSecurities = selectChartComparableSecurities(viewKey)(state) || []
    const config = selectChartConfig(viewKey)(state)
    chart.updateComparableSecurities(comparableSecurities, false, {
        comparableSecurityPanePlacements: config?.comparableSecurityPanePlacements,
        comparableSecurityPanePlacement: config?.comparableSecurityPanePlacement,
    })
}

function recordApplied(result: ChartCommandApplyResult, command: ChartCommand) {
    result.applied.push({
        op: String(command.op || ''),
        message: chartCommandSuccessMessage(command),
    })
}

function recordFailed(
    result: ChartCommandApplyResult,
    command: ChartCommand,
    reason: ChartCommandFailureReason,
) {
    result.failed.push({
        op: String(command.op || ''),
        message: chartCommandFailureMessage(command, reason),
        reason,
    })
}

/**
 * Chart ops act on the focused pane unless they carry `target_pane`. Reading the
 * store here (rather than trusting the captured viewKey) is what lets `focus_pane`
 * and a follow-up op land in the same batch.
 */
function resolvePaneIndexForViewKey(
    layout: NonNullable<ReturnType<typeof selectActiveLayout>>,
    layoutId: string,
    viewKey: string,
): number | undefined {
    const slots = orderedPaneSlots(layout)
    for (let index = 0; index < slots.length; index += 1) {
        const slot = slots[index]
        const chartId = layout.widgets?.[slot]?.settings?.chartId ?? `${layoutId}-${slot}`
        if (chartId === viewKey) return index + 1
    }
    return undefined
}

function resolveCommandViewKey(command: ChartCommand, focusedViewKey: string): string {
    const state = store.getState()
    const layout = selectActiveLayout(state)
    const layoutId = selectActiveLayoutId(state)
    if (!layout || !layoutId) return focusedViewKey

    const explicit = command.target_pane ?? command.pane_index ?? command.window
    const slot = resolvePaneSlot(layout, explicit ?? undefined)
    if (slot == null) return focusedViewKey

    const chartId = layout.widgets?.[slot]?.settings?.chartId
    return typeof chartId === 'string' && chartId ? chartId : `${layoutId}-${slot}`
}

async function runLayoutCommand(
    command: ChartCommand,
    result: ChartCommandApplyResult,
    deps: Pick<ChartCommandApplyDeps, 'dispatch' | 'dashboard' | 'setPrimarySecurity'>,
): Promise<void> {
    const normalized: ChartCommand = { ...command, op: normalizeLayoutOp(String(command.op || '')) }
    const outcome = await applyLayoutCommand(normalized as Record<string, unknown>, {
        dispatch: deps.dispatch,
        dashboard: deps.dashboard,
        setPrimarySecurity: deps.setPrimarySecurity,
        resolveSecurity: (exchange, code) =>
            resolveSecurityForCommand({ op: 'set_pane_symbol', exchange, code } as ChartCommand),
    })

    switch (outcome.status) {
        case 'applied':
            result.applied.push({
                op: normalized.op,
                message: layoutCommandSuccessMessage(normalized, outcome.detail),
            })
            return
        case 'noop':
            // Already in the requested state — not a failure worth a retry prompt.
            return
        case 'failed':
            result.failed.push({
                op: normalized.op,
                message: layoutCommandFailureMessage(normalized, outcome.reason),
                reason: outcome.reason,
            })
            return
    }
}

export async function applyChartCommands(
    commands: ChartCommand[],
    deps: ChartCommandApplyDeps,
): Promise<ChartCommandApplyResult> {
    const result = emptyChartCommandApplyResult()
    const { viewKey: focusedViewKey, chart: focusedChart, dispatch, setPrimarySecurity, dashboard } = deps
    if (!commands.length) return result

    if (!focusedViewKey) {
        commands.forEach((command) => recordFailed(result, command, 'chart_unavailable'))
        return result
    }

    const state = store.getState()
    const indicators = selectIndicators(state)
    const timeBuckets = selectTimeBuckets(state)

    for (const command of commands) {
        const op = String(command.op || '')

        if (op === LOAD_SAVED_LAYOUT_OP) {
            if (!deps.navigate) {
                recordFailed(result, command, 'chart_unavailable')
                continue
            }
            const targetLayoutId = resolveSavedLayoutId(store.getState(), command)
            if (!targetLayoutId) {
                recordFailed(result, command, 'not_found')
                continue
            }
            const activeLayoutId = selectActiveLayoutId(store.getState())
            if (activeLayoutId !== targetLayoutId) {
                deps.navigate(generatePath(ROUTES.chart, { layoutId: targetLayoutId }))
            }
            const layoutName = savedLayoutName(store.getState(), targetLayoutId) || targetLayoutId
            recordApplied(result, { ...command, name: layoutName })
            continue
        }

        if (isLayoutCommandOp(op)) {
            await runLayoutCommand(command, result, {
                dispatch,
                dashboard,
                setPrimarySecurity,
            })
            continue
        }

        // Resolved per command: a preceding focus_pane (or an explicit target_pane)
        // changes which chart the rest of the batch should act on.
        const viewKey = resolveCommandViewKey(command, focusedViewKey)
        const chart =
            viewKey === focusedViewKey ? focusedChart : deps.getChart?.(viewKey) ?? undefined

        switch (op) {
            case 'set_symbol': {
                const exchange = parseExchange(command.exchange)
                const code = String(command.code || '')
                if (exchange == null || !code) {
                    recordFailed(result, command, 'missing_fields')
                    break
                }
                const security = await getSecurityAsync(getSecurityKey(exchange, code))
                if (!security) {
                    recordFailed(result, command, 'not_found')
                    break
                }
                const layoutState = store.getState()
                const layout = selectActiveLayout(layoutState)
                const layoutId = selectActiveLayoutId(layoutState)
                const multiPane = layout != null && orderedPaneSlots(layout).length > 1
                const paneTarget =
                    command.target_pane ??
                    command.pane_index ??
                    command.window ??
                    (layout && layoutId && viewKey !== focusedViewKey
                        ? resolvePaneIndexForViewKey(layout, layoutId, viewKey)
                        : undefined)

                if (multiPane && layout && layoutId) {
                    await runLayoutCommand(
                        {
                            ...command,
                            op: 'set_pane_symbol',
                            pane: paneTarget,
                            exchange: security.exchange,
                            code: security.code,
                        },
                        result,
                        { dispatch, dashboard, setPrimarySecurity },
                    )
                    break
                }

                const key = getSecurityKey(security.exchange, security.code)
                setPrimarySecurity(key, [security], false, security.displayName || security.symbol)
                recordApplied(result, { ...command, code: security.displayName || security.symbol || code })
                break
            }
            case 'set_time_bucket': {
                // chartStore isn't persisted, so these arrive only after the super chart
                // configuration loads. Without this the user is told the timeframe does
                // not exist, which is wrong and unactionable.
                if (!timeBuckets.length) {
                    recordFailed(result, command, 'chart_unavailable')
                    break
                }
                const bucket = resolveTimeBucketForChart(timeBuckets, command, viewKey)
                if (!bucket) {
                    recordFailed(result, command, 'not_found')
                    break
                }
                const currentBucket = selectChartConfig(viewKey)(store.getState())?.timeBucket
                if (timeBucketsMatch(currentBucket, bucket)) {
                    break
                }
                dispatch(updateChartTimeBucket({ viewKey, timeBucket: bucket }))
                recordApplied(result, { ...command, code: bucket.name || bucket.code })
                break
            }
            case 'set_chart_type': {
                const chartType = resolveChartTypeFromCommand(command)
                if (chartType == null) {
                    recordFailed(result, command, 'not_found')
                    break
                }
                dispatch(updateChartType({ viewKey, chartType }))
                recordApplied(result, command)
                break
            }
            case 'add_indicator': {
                const rawCode = String(command.code || '')
                if (!rawCode) {
                    recordFailed(result, command, 'missing_fields')
                    break
                }
                if (!chart) {
                    recordFailed(result, command, 'chart_unavailable')
                    break
                }
                if (!indicators.length) {
                    recordFailed(result, command, 'chart_unavailable')
                    break
                }
                const template = resolveIndicatorTemplate(indicators, rawCode)
                if (!template) {
                    recordFailed(result, command, 'not_found')
                    break
                }
                const requestedPane = String(command.pane || 'overlay').toLowerCase() === 'overlay' ? 'overlay' : 'new'
                const pane = defaultIndicatorPane(template, requestedPane)
                const indicator = cloneIndicatorWithInputs(
                    template,
                    (command.inputs as Record<string, unknown>) || undefined,
                    pane,
                )
                await chart.addIndicator(indicator)
                recordApplied(result, { ...command, code: template.name || template.shortName || rawCode })
                break
            }
            case 'remove_indicator': {
                const seriesGroupId = findSeriesGroupId(viewKey, command)
                if (!seriesGroupId) {
                    recordFailed(result, command, 'not_found')
                    break
                }
                if (chart) {
                    chart.removeIndicatorSeriesGroupTree(seriesGroupId)
                } else {
                    dispatch(removeIndicator({ viewKey, seriesGrpId: seriesGroupId }))
                }
                recordApplied(result, command)
                break
            }
            case 'remove_all_indicators': {
                const config = selectChartConfig(viewKey)(store.getState())
                const indicatorGroupIds = Object.values(config?.seriesGroups || {})
                    .filter((group) =>
                        [SeriesGroupCategory.SERIES_INDICATOR, SeriesGroupCategory.SERIES_VOLUME].includes(
                            group.category,
                        ),
                    )
                    .map((group) => group.id)
                if (!indicatorGroupIds.length) {
                    recordFailed(result, command, 'nothing_to_change')
                    break
                }
                for (const seriesGroupId of indicatorGroupIds) {
                    if (chart) {
                        chart.removeIndicatorSeriesGroupTree(seriesGroupId)
                    } else {
                        dispatch(removeIndicator({ viewKey, seriesGrpId: seriesGroupId }))
                    }
                }
                recordApplied(result, command)
                break
            }
            case 'update_indicator_inputs': {
                const seriesGroupId = findSeriesGroupId(viewKey, command)
                const inputs = command.inputs
                if (!seriesGroupId) {
                    recordFailed(result, command, 'not_found')
                    break
                }
                if (!inputs || typeof inputs !== 'object') {
                    recordFailed(result, command, 'missing_fields')
                    break
                }
                const normalized: Record<string, string> = {}
                Object.entries(inputs as Record<string, unknown>).forEach(([key, value]) => {
                    normalized[sanitizeId(key)] = String(value ?? '')
                })
                if (chart) {
                    const existing = selectChartConfig(viewKey)(store.getState())?.seriesGroups[seriesGroupId]
                    chart.updateSeriesGroupInputs(
                        seriesGroupId,
                        { ...(existing?.inputValues || {}), ...normalized },
                        true,
                    )
                } else {
                    dispatch(updateGroupInputs({ viewKey, groupId: seriesGroupId, values: normalized }))
                }
                recordApplied(result, command)
                break
            }
            case 'add_comparison': {
                const security = await resolveSecurityForCommand(command)
                if (!security) {
                    recordFailed(result, command, 'not_found')
                    break
                }
                const current = selectChartComparableSecurities(viewKey)(store.getState()) || []
                const securityKey = getSecurityKey(security.exchange, security.code)
                if (current.some((item) => getSecurityKey(item.exchange, item.code) === securityKey)) {
                    syncComparableSecuritiesToChart(viewKey, chart)
                    recordApplied(result, {
                        ...command,
                        code: security.displayName || security.symbol || security.code,
                    })
                    break
                }
                const config = selectChartConfig(viewKey)(store.getState())
                const placements = buildComparableSecurityPanePlacements(
                    current,
                    config?.comparableSecurityPanePlacement,
                    config?.comparableSecurityPanePlacements,
                )
                dispatch(
                    updateComparableSecurities({
                        viewKey,
                        comparableSecurities: [...current, security],
                        comparableSecurityPanePlacements: [...placements, comparisonPlacement(command.pane)],
                    }),
                )
                syncComparableSecuritiesToChart(viewKey, chart)
                recordApplied(result, {
                    ...command,
                    code: security.displayName || security.symbol || security.code,
                })
                break
            }
            case 'remove_comparison': {
                const current = selectChartComparableSecurities(viewKey)(store.getState()) || []
                if (!current.length) {
                    recordFailed(result, command, 'nothing_to_change')
                    break
                }
                const exchange = parseExchange(command.exchange)
                const code = String(command.code || command.symbol || '').toUpperCase()
                const config = selectChartConfig(viewKey)(store.getState())
                const placements = buildComparableSecurityPanePlacements(
                    current,
                    config?.comparableSecurityPanePlacement,
                    config?.comparableSecurityPanePlacements,
                )
                const index = code
                    ? current.findIndex(
                          (item) =>
                              item.code?.toUpperCase() === code &&
                              (exchange == null || item.exchange === exchange),
                      )
                    : current.length - 1
                if (index < 0) {
                    recordFailed(result, command, 'not_found')
                    break
                }
                const next = current.filter((_, i) => i !== index)
                const nextPlacements = placements.filter((_, i) => i !== index)
                dispatch(
                    updateComparableSecurities({
                        viewKey,
                        comparableSecurities: next,
                        comparableSecurityPanePlacements: nextPlacements,
                    }),
                )
                syncComparableSecuritiesToChart(viewKey, chart)
                recordApplied(result, command)
                break
            }
            case 'set_scale_mode': {
                if (!chart) {
                    recordFailed(result, command, 'chart_unavailable')
                    break
                }
                const mode = SCALE_MODES[String(command.mode || '').toLowerCase().replace(/[\s-]+/g, '_')]
                if (mode == null) {
                    recordFailed(result, command, 'invalid_value')
                    break
                }
                chart.updatePriceScaleMode(mode)
                recordApplied(result, command)
                break
            }
            case 'set_invert_scale': {
                if (!chart) {
                    recordFailed(result, command, 'chart_unavailable')
                    break
                }
                chart.updateInvertScale(Boolean(command.invert))
                recordApplied(result, command)
                break
            }
            case 'move_price_scale': {
                if (!chart) {
                    recordFailed(result, command, 'chart_unavailable')
                    break
                }
                chart.movePrimaryPriceScale()
                recordApplied(result, command)
                break
            }
            case 'update_chart_settings': {
                const config = selectChartConfig(viewKey)(store.getState())
                if (!config) {
                    recordFailed(result, command, 'chart_unavailable')
                    break
                }
                const patch: Record<string, unknown> = {}
                ;(Object.keys(SETTINGS_GROUPS) as SettingsGroupKey[]).forEach((group) => {
                    const incoming = command[group]
                    if (!incoming || typeof incoming !== 'object') return
                    const field = SETTINGS_GROUPS[group]
                    patch[field] = { ...((config as any)[field] || {}), ...(incoming as object) }
                })
                if (!Object.keys(patch).length) {
                    recordFailed(result, command, 'missing_fields')
                    break
                }
                dispatch(updateChartConfig({ viewKey, config: patch }))
                recordApplied(result, command)
                break
            }
            case 'update_series_style': {
                if (!chart) {
                    recordFailed(result, command, 'chart_unavailable')
                    break
                }
                const primarySeries = chart.primarySeriesGroup?.getPrimarySeries()
                const rawOptions = command.options ?? command.style ?? command.colors
                if (!primarySeries || !rawOptions || typeof rawOptions !== 'object') {
                    recordFailed(result, command, 'missing_fields')
                    break
                }
                primarySeries.updateOptions(rawOptions as Record<string, unknown>)
                recordApplied(result, command)
                break
            }
            case 'set_date_range': {
                if (!chart) {
                    recordFailed(result, command, 'chart_unavailable')
                    break
                }
                const range = resolveDateRange(selectDateRanges(store.getState()) || [], String(command.code || ''))
                if (!range) {
                    recordFailed(result, command, 'not_found')
                    break
                }
                chart.setSelectedDateRange(range)
                recordApplied(result, command)
                break
            }
            case 'goto_date': {
                if (!chart) {
                    recordFailed(result, command, 'chart_unavailable')
                    break
                }
                const time = parseTimeSeconds(command.date ?? command.time)
                if (time == null) {
                    recordFailed(result, command, 'invalid_value')
                    break
                }
                chart.goToDate(time, true)
                recordApplied(result, command)
                break
            }
            case 'set_visible_range': {
                if (!chart) {
                    recordFailed(result, command, 'chart_unavailable')
                    break
                }
                const from = parseTimeSeconds(command.from)
                const to = parseTimeSeconds(command.to)
                if (from == null || to == null || from >= to) {
                    recordFailed(result, command, 'invalid_value')
                    break
                }
                chart.setVisibleRange(from as Time, to as Time)
                recordApplied(result, command)
                break
            }
            case 'zoom': {
                if (!chart) {
                    recordFailed(result, command, 'chart_unavailable')
                    break
                }
                const direction = String(command.direction || 'in').toLowerCase()
                if (direction !== 'in' && direction !== 'out') {
                    recordFailed(result, command, 'invalid_value')
                    break
                }
                if (direction === 'out') chart.zoomOut()
                else chart.zoomIn()
                recordApplied(result, command)
                break
            }
            case 'pan': {
                if (!chart) {
                    recordFailed(result, command, 'chart_unavailable')
                    break
                }
                const direction = String(command.direction || '').toLowerCase()
                const fast = Boolean(command.fast)
                if (direction === 'left') chart.panLeft(fast)
                else if (direction === 'right') chart.panRight(fast)
                else {
                    recordFailed(result, command, 'invalid_value')
                    break
                }
                recordApplied(result, command)
                break
            }
            case 'reset_view': {
                if (!chart) {
                    recordFailed(result, command, 'chart_unavailable')
                    break
                }
                chart.resetTimeScale()
                recordApplied(result, command)
                break
            }
            case 'goto_latest': {
                if (!chart) {
                    recordFailed(result, command, 'chart_unavailable')
                    break
                }
                chart.gotoCurrentTime()
                recordApplied(result, command)
                break
            }
            case 'add_drawing': {
                if (!chart) {
                    recordFailed(result, command, 'chart_unavailable')
                    break
                }
                const placement = buildDrawingPlacement(command, chart)
                if (!placement) {
                    recordFailed(result, command, 'not_found')
                    break
                }
                const paneId = chart.getPrimaryPaneId()
                const [seriesId] = paneId ? chart.getDrawingSeries(paneId) : []
                chart.drawingManager.addDrawing(
                    placement.type,
                    DrawingDisplayType.DRAWING,
                    placement.options,
                    (command.settings as Record<string, unknown>) || {},
                    undefined,
                    placement.initialState,
                    paneId,
                    seriesId,
                )
                recordApplied(result, command)
                break
            }
            case 'remove_drawing': {
                if (!chart) {
                    recordFailed(result, command, 'chart_unavailable')
                    break
                }
                const drawingId = String(command.id || '')
                if (!drawingId) {
                    recordFailed(result, command, 'missing_fields')
                    break
                }
                chart.drawingManager.removeDrawing(drawingId)
                recordApplied(result, command)
                break
            }
            default:
                recordFailed(result, command, 'unsupported_op')
                break
        }
    }

    return result
}
